import { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { isNil } from 'lodash';
// Import Types
import type { PlayersDataTableProps } from 'components/common/PlayersDataTable/PlayersDataTable';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Services
import { CampaignHttpService } from 'services/http';
// Import Components
import PlayersDataTable from 'components/common/PlayersDataTable';
// Import Constants
import { CampaignActivityOpenedMailPlayersTableConfig } from './config';

const CampaignActivityMetricsPlayersWhoOpenedMail = ({
    campaignId,
    startDate,
    endDate,
    setIsDisableDownload,
    isLoadingSchedule,
    isActive,
}) => {
    const { doPostRequest } = useRequest();
    const [isLoadingPlayers, setIsLoadingPlayers] = useState(true);
    const [isRequestBodyChanged, setIsRequestBodyChanged] = useState(false);
    const [players, setPlayers] = useState<PlayersDataTableProps['data']>(null);

    const { getCampaignEmailTrackInfoOpenRequest } = useCancelableRequests(
        { getCampaignEmailTrackInfoOpenRequest: CampaignHttpService.getCampaignEmailTrackInfoOpen },
        { takeLatest: ['getCampaignEmailTrackInfoOpenRequest'] },
    );

    useEffect(() => {
        if (!isLoadingSchedule && isActive && isRequestBodyChanged) {
            setIsLoadingPlayers(true);
            setIsRequestBodyChanged(false);

            doPostRequest(getCampaignEmailTrackInfoOpenRequest.request, {
                requestBody: { CampaignId: campaignId, StartDate: startDate, EndDate: endDate },
                successCallback: (Data) => {
                    setPlayers(Data);
                    setIsDisableDownload(isNil(Data?.Count) || Data?.Count === 0);
                },
            }).then(({ IsCanceled }) => {
                // A superseded call must not hide the loader of the call that replaced it
                if (!IsCanceled) {
                    setIsLoadingPlayers(false);
                }
            });
        }
    }, [
        isActive,
        isRequestBodyChanged,
        isLoadingSchedule,
        campaignId,
        startDate,
        endDate,
        setIsDisableDownload,
        getCampaignEmailTrackInfoOpenRequest,
        doPostRequest,
    ]);

    useEffect(() => {
        setIsRequestBodyChanged(true);
    }, [campaignId, startDate, endDate]);

    return (
        <PlayersDataTable
            key="ClientId"
            data={players}
            isLoading={isLoadingPlayers}
            config={CampaignActivityOpenedMailPlayersTableConfig}
        />
    );
};

CampaignActivityMetricsPlayersWhoOpenedMail.propTypes = {
    campaignId: PropTypes.string.isRequired,
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    setIsDisableDownload: PropTypes.func.isRequired,
    isLoadingSchedule: PropTypes.bool.isRequired,
    isActive: PropTypes.bool,
};

CampaignActivityMetricsPlayersWhoOpenedMail.defaultProps = {
    isActive: true,
};

export default memo(CampaignActivityMetricsPlayersWhoOpenedMail);
